import React, { useState, useEffect, useCallback } from 'react';
import { workflowConfigsApi, holidaysApi, systemSettingsApi, exclusionRulesApi, emailApi, adminApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Gear, House, Briefcase, Phone, CalendarX, Warning, Plus, Trash, CalendarBlank, UserCirclePlus, ShieldSlash, EnvelopeSimple, PencilSimple, Eye, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';

const SCHEDULE_TYPES = [
  { value: 'office', label: 'Ufficio', icon: House, color: '#0A0A0A', description: 'Presenze in ufficio' },
  { value: 'smart_working', label: 'Smart Working', icon: Briefcase, color: '#002FA7', description: 'Richieste di lavoro da remoto' },
  { value: 'leave', label: 'Ferie/Assenza', icon: CalendarX, color: '#F59E0B', description: 'Richieste di ferie e permessi' },
  { value: 'on_call', label: 'Reperibilità', icon: Phone, color: '#FF2A00', description: 'Turni di reperibilità' },
];

const ITALIAN_HOLIDAYS_DEFAULT = [
  { date: '-01-01', name: 'Capodanno' },
  { date: '-01-06', name: 'Epifania' },
  { date: '-04-25', name: 'Festa della Liberazione' },
  { date: '-05-01', name: 'Festa dei Lavoratori' },
  { date: '-06-02', name: 'Festa della Repubblica' },
  { date: '-08-15', name: 'Ferragosto' },
  { date: '-11-01', name: 'Ognissanti' },
  { date: '-12-08', name: 'Immacolata Concezione' },
  { date: '-12-25', name: 'Natale' },
  { date: '-12-26', name: 'Santo Stefano' },
];

const EmailTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try { const { data } = await emailApi.getTemplates(); setTemplates(data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await emailApi.updateTemplate(editing.key, { subject: editSubject, body: editBody, label: editing.label });
      toast.success('Template salvato');
      setEditing(null);
      fetchTemplates();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const handleReset = async (key) => {
    try { await emailApi.resetTemplate(key); toast.success('Template ripristinato'); fetchTemplates(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handlePreview = async (key) => {
    try {
      const { data } = await emailApi.previewTemplate(key);
      setPreviewHtml(data.html);
      setPreviewOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const globals = templates.filter(t => t.key.startsWith('_'));
  const notifTemplates = templates.filter(t => !t.key.startsWith('_'));

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="email-templates-section">
      <div className="px-6 py-4 border-b border-[#E4E4E7]">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <EnvelopeSimple size={20} weight="bold" /> Notifiche Email & Template
        </h2>
        <p className="text-sm text-[#52525B] mt-1">Personalizza i template email. Header e footer sono condivisi da tutte le notifiche.</p>
      </div>
      <div className="p-6 space-y-6">
        {/* Test email */}
        <div className="flex flex-col sm:flex-row items-end gap-3">
          <div className="flex-1 w-full">
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Invia email di test</Label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="email@azienda.com"
              className="rounded-none border-[#E4E4E7]" data-testid="test-email-input" />
          </div>
          <Button disabled={sendingTest || !testEmail} onClick={async () => {
            setSendingTest(true);
            try { await emailApi.testEmail(testEmail); toast.success('Email di test inviata!'); }
            catch (err) { toast.error(formatApiError(err)); } finally { setSendingTest(false); }
          }} className="bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none w-full sm:w-auto" data-testid="send-test-email-btn">
            {sendingTest ? 'Invio...' : 'Invia Test'}
          </Button>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-[#A1A1AA]">Caricamento template...</div> : (
          <>
            {/* Global Header/Footer */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B] mb-2">Scheletro Globale (Header & Footer)</p>
              <div className="space-y-2">
                {globals.map(tpl => (
                  <div key={tpl.key} className="flex items-center justify-between p-3 border border-[#E4E4E7] hover:bg-[#FAFAFA]">
                    <div>
                      <p className="text-sm font-medium">{tpl.label}</p>
                      {!tpl.is_default && <span className="text-[10px] text-[#002FA7] font-bold uppercase">Personalizzato</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditing(tpl); setEditSubject(tpl.subject); setEditBody(tpl.body); }}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A]" title="Modifica">
                        <PencilSimple size={14} weight="bold" /></button>
                      {!tpl.is_default && <button onClick={() => handleReset(tpl.key)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#F59E0B] hover:text-[#F59E0B]" title="Ripristina">
                        <ArrowCounterClockwise size={14} weight="bold" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notification Templates */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B] mb-2">Template Notifiche ({notifTemplates.length})</p>
              <div className="space-y-2">
                {notifTemplates.map(tpl => (
                  <div key={tpl.key} className="flex items-center justify-between p-3 border border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`tpl-${tpl.key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tpl.label}</p>
                      <p className="text-[10px] text-[#A1A1AA] font-mono truncate">{tpl.subject}</p>
                      {!tpl.is_default && <span className="text-[10px] text-[#002FA7] font-bold uppercase">Personalizzato</span>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => handlePreview(tpl.key)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#002FA7] hover:text-[#002FA7]" title="Anteprima">
                        <Eye size={14} weight="bold" /></button>
                      <button onClick={() => { setEditing(tpl); setEditSubject(tpl.subject); setEditBody(tpl.body); }}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A]" title="Modifica">
                        <PencilSimple size={14} weight="bold" /></button>
                      {!tpl.is_default && <button onClick={() => handleReset(tpl.key)}
                        className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#F59E0B] hover:text-[#F59E0B]" title="Ripristina">
                        <ArrowCounterClockwise size={14} weight="bold" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#0A0A0A] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Modifica Template: {editing?.label}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 mt-4">
              {editing.key.startsWith('_') ? null : (
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Oggetto</Label>
                  <Input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                    className="rounded-none border-[#E4E4E7] font-mono text-sm" data-testid="edit-tpl-subject" />
                  <p className="text-[10px] text-[#A1A1AA] mt-1">Variabili: {'{{variabile}}'}</p>
                </div>
              )}
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Corpo HTML</Label>
                <Textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={12}
                  className="rounded-none border-[#E4E4E7] font-mono text-xs" data-testid="edit-tpl-body" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button disabled={saving} onClick={handleSave} className="flex-1 bg-[#0A0A0A] text-white hover:bg-[#333] rounded-none" data-testid="save-tpl-btn">
                  {saving ? 'Salvataggio...' : 'Salva Template'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#0A0A0A] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Anteprima Email</DialogTitle></DialogHeader>
          <div className="mt-4 border border-[#E4E4E7] bg-[#F9FAFB] p-4">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });
  const [addingHoliday, setAddingHoliday] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);

  // System settings state
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [savingRegistration, setSavingRegistration] = useState(false);

  // Exclusion rules state
  const [exclusionRules, setExclusionRules] = useState([]);
  const [newRuleA, setNewRuleA] = useState('');
  const [newRuleB, setNewRuleB] = useState('');
  const [newRuleDesc, setNewRuleDesc] = useState('');
  const [addingRule, setAddingRule] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await workflowConfigsApi.getAll();
      const configMap = {};
      data.forEach((config) => { configMap[config.request_type] = config; });
      SCHEDULE_TYPES.forEach((type) => {
        if (!configMap[type.value]) {
          configMap[type.value] = { request_type: type.value, requires_team_leader: true, requires_manager: true };
        }
      });
      setConfigs(configMap);
    } catch (error) {
      console.error('Failed to fetch workflow configs:', error);
      toast.error('Errore nel caricamento configurazioni');
    } finally { setLoading(false); }
  }, []);

  const fetchHolidays = useCallback(async () => {
    setHolidaysLoading(true);
    try {
      const { data } = await holidaysApi.getAll(selectedYear);
      setHolidays(data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    } finally { setHolidaysLoading(false); }
  }, [selectedYear]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);
  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // Fetch system settings
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const { data } = await systemSettingsApi.getAll();
        if (data.allow_registration !== undefined) setAllowRegistration(data.allow_registration);
      } catch (e) { console.error(e); }
    };
    fetchSystemSettings();
  }, []);

  // Fetch exclusion rules
  const fetchExclusionRules = useCallback(async () => {
    try {
      const { data } = await exclusionRulesApi.getAll();
      setExclusionRules(data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchExclusionRules(); }, [fetchExclusionRules]);

  const handleToggle = async (requestType, field, value) => {
    const newConfig = { ...configs[requestType], [field]: value };
    setConfigs((prev) => ({ ...prev, [requestType]: newConfig }));
    setSaving((prev) => ({ ...prev, [requestType]: true }));
    try {
      await workflowConfigsApi.create({
        request_type: requestType,
        requires_team_leader: newConfig.requires_team_leader,
        requires_manager: newConfig.requires_manager,
      });
      toast.success('Configurazione aggiornata');
    } catch (err) {
      fetchConfigs();
      toast.error(formatApiError(err));
    } finally { setSaving((prev) => ({ ...prev, [requestType]: false })); }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date || !newHoliday.name) return;
    setAddingHoliday(true);
    try {
      const year = parseInt(newHoliday.date.substring(0, 4));
      await holidaysApi.create({ date: newHoliday.date, name: newHoliday.name, year });
      toast.success('Festività aggiunta');
      setNewHoliday({ date: '', name: '' });
      fetchHolidays();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddingHoliday(false); }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await holidaysApi.delete(id);
      toast.success('Festività eliminata');
      fetchHolidays();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkAddDefaults = async () => {
    setBulkAdding(true);
    try {
      const existingDates = new Set(holidays.map(h => h.date));
      const toAdd = ITALIAN_HOLIDAYS_DEFAULT
        .map(h => ({ date: `${selectedYear}${h.date}`, name: h.name, year: selectedYear }))
        .filter(h => !existingDates.has(h.date));
      if (toAdd.length === 0) {
        toast.info('Tutte le festività standard sono già presenti');
        setBulkAdding(false);
        return;
      }
      await holidaysApi.bulkCreate(toAdd);
      toast.success(`${toAdd.length} festività aggiunte`);
      fetchHolidays();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkAdding(false); }
  };

  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div data-testid="settings-page">
      <Toaster position="top-right" richColors />

      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Impostazioni</h1>
        <p className="text-[#52525B] mt-1">Configura i flussi di approvazione, festività e impostazioni di sistema</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* ============ COMPANY HOLIDAYS ============ */}
          <div className="bg-white border border-[#E4E4E7]" data-testid="holidays-section">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <CalendarBlank size={20} weight="bold" />
                Festività e Chiusure Aziendali
              </h2>
              <p className="text-sm text-[#52525B] mt-1">
                Gestisci le festività aziendali per il calcolo corretto dei giorni lavorativi nei report mensili.
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Year selector + bulk add */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Anno</Label>
                  <div className="flex border border-[#E4E4E7]">
                    <button onClick={() => setSelectedYear(y => y - 1)}
                      className="px-3 py-2 text-sm hover:bg-[#F4F4F5] transition-colors" data-testid="holidays-prev-year">&larr;</button>
                    <span className="px-4 py-2 text-sm font-bold border-x border-[#E4E4E7]" data-testid="holidays-year">{selectedYear}</span>
                    <button onClick={() => setSelectedYear(y => y + 1)}
                      className="px-3 py-2 text-sm hover:bg-[#F4F4F5] transition-colors" data-testid="holidays-next-year">&rarr;</button>
                  </div>
                </div>
                <Button onClick={handleBulkAddDefaults} disabled={bulkAdding}
                  variant="outline" className="rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                  data-testid="bulk-add-holidays-btn">
                  <Plus size={16} weight="bold" className="mr-2" />
                  {bulkAdding ? 'Aggiunta...' : `Aggiungi Festività Italiane ${selectedYear}`}
                </Button>
              </div>

              {/* Add single holiday form */}
              <form onSubmit={handleAddHoliday} className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data</Label>
                  <Input type="date" value={newHoliday.date}
                    onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })}
                    className="rounded-none border-[#E4E4E7] w-44" data-testid="holiday-date-input" required />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Descrizione</Label>
                  <Input value={newHoliday.name}
                    onChange={e => setNewHoliday({ ...newHoliday, name: e.target.value })}
                    placeholder="Es: Chiusura aziendale estiva"
                    className="rounded-none border-[#E4E4E7]" data-testid="holiday-name-input" required />
                </div>
                <Button type="submit" disabled={addingHoliday}
                  className="bg-[#0A0A0A] text-white hover:bg-[#333] rounded-none" data-testid="add-holiday-btn">
                  <Plus size={16} weight="bold" className="mr-2" />
                  {addingHoliday ? 'Aggiunta...' : 'Aggiungi'}
                </Button>
              </form>

              {/* Holidays list */}
              {holidaysLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="w-6 h-6 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : sortedHolidays.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-[#E4E4E7]">
                  <CalendarBlank size={32} className="mx-auto text-[#E4E4E7] mb-2" />
                  <p className="text-sm text-[#52525B]">Nessuna festività definita per il {selectedYear}.</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">Usa il pulsante sopra per aggiungere le festività italiane standard.</p>
                </div>
              ) : (
                <div className="border border-[#E4E4E7] overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-[#0A0A0A] bg-white">
                        <th className="py-3 px-4 text-left text-xs font-bold uppercase tracking-wider">Data</th>
                        <th className="py-3 px-4 text-left text-xs font-bold uppercase tracking-wider">Descrizione</th>
                        <th className="py-3 px-4 text-right text-xs font-bold uppercase tracking-wider">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHolidays.map(h => (
                        <tr key={h.id} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`holiday-row-${h.id}`}>
                          <td className="py-3 px-4 font-mono text-sm font-medium">
                            {new Date(h.date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'long' })}
                          </td>
                          <td className="py-3 px-4 text-sm">{h.name}</td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => handleDeleteHoliday(h.id)}
                              className="w-8 h-8 inline-flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                              data-testid={`delete-holiday-${h.id}`} title="Elimina">
                              <Trash size={14} weight="bold" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 bg-[#FAFAFA] border-t border-[#E4E4E7]">
                    <p className="text-xs text-[#52525B]">{sortedHolidays.length} festività per il {selectedYear}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ============ APPROVAL WORKFLOW ============ */}
          <div className="bg-white border border-[#E4E4E7]">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <Gear size={20} weight="bold" />
                Configurazione Flusso di Approvazione
              </h2>
              <p className="text-sm text-[#52525B] mt-1">
                Configura quali livelli di approvazione sono necessari per ogni tipo di richiesta.
              </p>
            </div>

            <div className="divide-y divide-[#E4E4E7]">
              {SCHEDULE_TYPES.map((type) => {
                const Icon = type.icon;
                const config = configs[type.value] || {};
                const isSaving = saving[type.value];

                return (
                  <div key={type.value} className="p-6" data-testid={`workflow-config-${type.value}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${type.color}10` }}>
                        <Icon size={24} weight="bold" style={{ color: type.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{type.label}</h3>
                        <p className="text-sm text-[#52525B] mb-4">{type.description}</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E4E4E7]">
                            <div>
                              <Label className="font-medium">Approvazione Team Leader</Label>
                              <p className="text-xs text-[#52525B]">Primo livello di approvazione</p>
                            </div>
                            <Switch
                              checked={config.requires_team_leader}
                              onCheckedChange={(checked) => handleToggle(type.value, 'requires_team_leader', checked)}
                              disabled={isSaving}
                              data-testid={`toggle-team-leader-${type.value}`}
                            />
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E4E4E7]">
                            <div>
                              <Label className="font-medium">Approvazione Manager</Label>
                              <p className="text-xs text-[#52525B]">Secondo livello di approvazione</p>
                            </div>
                            <Switch
                              checked={config.requires_manager}
                              onCheckedChange={(checked) => handleToggle(type.value, 'requires_manager', checked)}
                              disabled={isSaving}
                              data-testid={`toggle-manager-${type.value}`}
                            />
                          </div>
                        </div>

                        {!config.requires_team_leader && !config.requires_manager && (
                          <div className="mt-4 p-3 bg-[#F59E0B]/5 border border-[#F59E0B] flex items-center gap-2">
                            <Warning className="text-[#F59E0B]" size={16} weight="bold" />
                            <p className="text-sm text-[#F59E0B]">
                              Nessuna approvazione richiesta - le richieste verranno approvate automaticamente.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ============ REGISTRATION CONTROL ============ */}
          <div className="bg-white border border-[#E4E4E7]" data-testid="registration-section">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <UserCirclePlus size={20} weight="bold" />
                Controllo Registrazione
              </h2>
              <p className="text-sm text-[#52525B] mt-1">
                Abilita o disabilita la possibilità per nuovi utenti di registrarsi autonomamente.
              </p>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E4E4E7]">
                <div>
                  <Label className="font-medium">Registrazione pubblica</Label>
                  <p className="text-xs text-[#52525B]">Se disabilitata, solo l'accesso tramite Entra ID o creazione manuale da parte dell'admin sarà possibile</p>
                </div>
                <Switch
                  checked={allowRegistration}
                  onCheckedChange={async (checked) => {
                    setSavingRegistration(true);
                    setAllowRegistration(checked);
                    try {
                      await systemSettingsApi.update('allow_registration', checked);
                      toast.success(checked ? 'Registrazione abilitata' : 'Registrazione disabilitata');
                    } catch (err) {
                      setAllowRegistration(!checked);
                      toast.error(formatApiError(err));
                    } finally { setSavingRegistration(false); }
                  }}
                  disabled={savingRegistration}
                  data-testid="toggle-registration"
                />
              </div>
            </div>
          </div>

          {/* ============ EXCLUSION RULES ============ */}
          <div className="bg-white border border-[#E4E4E7]" data-testid="exclusion-rules-section">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <ShieldSlash size={20} weight="bold" />
                Regole di Mutua Esclusione
              </h2>
              <p className="text-sm text-[#52525B] mt-1">
                Definisci quali tipi di turno non possono coesistere nello stesso giorno per un dipendente.
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Add new rule */}
              <div className="flex items-end gap-3 flex-wrap">
                <div className="min-w-[150px]">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo A</Label>
                  <Select value={newRuleA} onValueChange={setNewRuleA}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="rule-type-a"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SCHEDULE_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="rounded-none">{t.label}</SelectItem>)}
                      <SelectItem value="permission" className="rounded-none">Permesso</SelectItem>
                      <SelectItem value="overtime" className="rounded-none">Straordinario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="pb-2 text-sm font-bold text-[#52525B]">esclude</span>
                <div className="min-w-[150px]">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo B</Label>
                  <Select value={newRuleB} onValueChange={setNewRuleB}>
                    <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="rule-type-b"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SCHEDULE_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="rounded-none">{t.label}</SelectItem>)}
                      <SelectItem value="permission" className="rounded-none">Permesso</SelectItem>
                      <SelectItem value="overtime" className="rounded-none">Straordinario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Descrizione</Label>
                  <Input value={newRuleDesc} onChange={e => setNewRuleDesc(e.target.value)}
                    placeholder="Es: Ferie e Presenza si escludono"
                    className="rounded-none border-[#E4E4E7]" data-testid="rule-description" />
                </div>
                <Button disabled={addingRule || !newRuleA || !newRuleB || newRuleA === newRuleB}
                  onClick={async () => {
                    setAddingRule(true);
                    try {
                      await exclusionRulesApi.create(newRuleA, newRuleB, newRuleDesc);
                      toast.success('Regola aggiunta');
                      setNewRuleA(''); setNewRuleB(''); setNewRuleDesc('');
                      fetchExclusionRules();
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setAddingRule(false); }
                  }}
                  className="bg-[#0A0A0A] text-white hover:bg-[#333] rounded-none" data-testid="add-rule-btn">
                  <Plus size={16} weight="bold" className="mr-1" /> Aggiungi
                </Button>
              </div>

              {/* Rules list */}
              {exclusionRules.length === 0 ? (
                <p className="text-sm text-[#52525B] py-4 text-center">Nessuna regola definita. Tutti i tipi di turno possono coesistere.</p>
              ) : (
                <div className="border border-[#E4E4E7]">
                  {exclusionRules.map(rule => {
                    const labelA = [...SCHEDULE_TYPES, {value:'permission',label:'Permesso'},{value:'overtime',label:'Straordinario'}].find(t => t.value === rule.type_a)?.label || rule.type_a;
                    const labelB = [...SCHEDULE_TYPES, {value:'permission',label:'Permesso'},{value:'overtime',label:'Straordinario'}].find(t => t.value === rule.type_b)?.label || rule.type_b;
                    return (
                      <div key={rule.id} className="flex items-center justify-between p-3 border-b border-[#E4E4E7] last:border-b-0 hover:bg-[#FAFAFA]" data-testid={`rule-${rule.id}`}>
                        <div className="flex items-center gap-3">
                          <Switch checked={rule.enabled}
                            onCheckedChange={async (v) => {
                              try { await exclusionRulesApi.update(rule.id, v); fetchExclusionRules(); }
                              catch (err) { toast.error(formatApiError(err)); }
                            }} />
                          <div>
                            <p className="text-sm font-medium">
                              <span className="font-bold">{labelA}</span>
                              <span className="text-[#A1A1AA] mx-2">&#8596;</span>
                              <span className="font-bold">{labelB}</span>
                            </p>
                            {rule.description && <p className="text-xs text-[#52525B]">{rule.description}</p>}
                          </div>
                        </div>
                        <button onClick={async () => {
                            try { await exclusionRulesApi.delete(rule.id); toast.success('Regola eliminata'); fetchExclusionRules(); }
                            catch (err) { toast.error(formatApiError(err)); }
                          }}
                          className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
                          data-testid={`delete-rule-${rule.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ============ EMAIL TEMPLATES ============ */}
          <EmailTemplatesSection />

          {/* ============ MAINTENANCE ============ */}
          <div className="bg-white border border-[#E4E4E7]" data-testid="maintenance-section">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                <Gear size={20} weight="bold" />
                Manutenzione
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E4E4E7]">
                <div>
                  <Label className="font-medium">Pulizia dati orfani</Label>
                  <p className="text-xs text-[#52525B]">Rimuove turni, richieste e anomalie associate a utenti eliminati</p>
                </div>
                <Button onClick={async () => {
                  try {
                    const { data } = await adminApi.cleanupOrphans();
                    const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
                    toast.success(total > 0 ? `Rimossi ${total} record orfani` : 'Nessun dato orfano trovato');
                  } catch (err) { toast.error(formatApiError(err)); }
                }} variant="outline" className="rounded-none border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white"
                  data-testid="cleanup-orphans-btn">
                  Esegui Pulizia
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
